import React from 'react';
import { ShieldCheck, UserCheck, FileText, Mail } from 'lucide-react'; // Optional icons

const GDPRCompliance = () => {
  const lastUpdatedDate = "May 29, 2025"; // REPLACE WITH ACTUAL LAST UPDATED DATE
  const dpoEmail = "dpo@tabdivider.com"; // REPLACE with your DPO or privacy contact email

  // Helper for consistent section styling
  const Section = ({ title, children, id }) => (
    <section id={id} className="mb-8 scroll-mt-20"> {/* scroll-mt for anchor links */}
      <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-300 dark:border-gray-700 pb-2">
        {title}
      </h2>
      <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
        {children}
      </div>
    </section>
  );

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <header className="mb-10 text-center">
          <ShieldCheck className="h-16 w-16 mx-auto mb-4 text-green-600 dark:text-green-400" />
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white">
            <span className="text-blue-600 dark:text-blue-400">TabDivider</span> & GDPR Compliance
          </h1>
          <p className="mt-4 text-md sm:text-lg text-gray-600 dark:text-gray-400">
            Our Commitment to Your Data Protection Rights under GDPR
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
            Last Updated: {lastUpdatedDate}
          </p>
        </header>

        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-6 sm:p-8 lg:p-10">
          <Section title="1. Our Commitment to GDPR" id="commitment">
            <p>
              TabDivider ("we," "us," or "our") is dedicated to protecting the privacy and security of our users' personal data. We are committed to ensuring our practices align with the General Data Protection Regulation (GDPR) (EU) 2016/679, especially for users residing in the European Union (EU) and the European Economic Area (EEA) who use our bill splitting and shared expense management services.
            </p>
            <p>
              This page outlines our approach to GDPR compliance and explains the rights you have as a data subject. For a comprehensive understanding of how we collect, use, and safeguard your personal data, please refer to our full <a href="/privacy-policy" className="text-blue-600 dark:text-blue-400 hover:underline font-medium">Privacy Policy</a>.
            </p>
          </Section>

          <Section title="2. What is GDPR?" id="what-is-gdpr">
            <p>
              The General Data Protection Regulation (GDPR) is a comprehensive data protection law in the European Union that strengthens and unifies data protection for all individuals within the EU. It grants individuals greater control over their personal data and establishes new obligations for organizations that collect, handle, or analyze personal data.
            </p>
          </Section>

          <Section title="3. Data Processing Principles" id="processing-principles">
            <p>
              TabDivider adheres to the core principles of data processing as outlined in the GDPR:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2">
              <li><strong>Lawfulness, Fairness, and Transparency:</strong> We process personal data lawfully, fairly, and in a transparent manner.</li>
              <li><strong>Purpose Limitation:</strong> We collect personal data for specified, explicit, and legitimate purposes and do not further process it in a manner incompatible with those purposes.</li>
              <li><strong>Data Minimization:</strong> We ensure that personal data collected is adequate, relevant, and limited to what is necessary in relation to the purposes for which it is processed.</li>
              <li><strong>Accuracy:</strong> We take reasonable steps to ensure that personal data is accurate and, where necessary, kept up to date.</li>
              <li><strong>Storage Limitation:</strong> We keep personal data in a form which permits identification of data subjects for no longer than is necessary for the purposes for which the personal data is processed.</li>
              <li><strong>Integrity and Confidentiality:</strong> We process personal data in a manner that ensures appropriate security, including protection against unauthorized or unlawful processing and against accidental loss, destruction, or damage.</li>
            </ul>
          </Section>

          <Section title="4. Your Rights as a Data Subject under GDPR" id="data-subject-rights">
            <div className="flex items-start mb-4">
                <UserCheck className="h-8 w-8 mr-3 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
                <p className="font-semibold text-lg">
                If you are located in the EU/EEA, the GDPR grants you specific rights regarding your personal data. TabDivider is committed to facilitating the exercise of these rights:
                </p>
            </div>
            <ul className="space-y-3 pl-4">
              {[
                { title: "The Right to Access", description: "You have the right to request access to the personal data we hold about you and to receive a copy of that data." },
                { title: "The Right to Rectification", description: "You have the right to request the correction of inaccurate or incomplete personal data we hold about you." },
                { title: "The Right to Erasure ('Right to be Forgotten')", description: "You have the right to request the deletion of your personal data under certain circumstances (e.g., if the data is no longer necessary for the purposes for which it was collected)." },
                { title: "The Right to Restrict Processing", description: "You have the right to request the restriction of processing of your personal data under certain conditions (e.g., if you contest the accuracy of the data)." },
                { title: "The Right to Data Portability", description: "You have the right to receive the personal data you have provided to us in a structured, commonly used, and machine-readable format and have the right to transmit that data to another controller where technically feasible." },
                { title: "The Right to Object to Processing", description: "You have the right to object to the processing of your personal data under certain circumstances, including for direct marketing purposes." },
                { title: "Rights Related to Automated Decision Making and Profiling", description: "You have the right not to be subject to a decision based solely on automated processing, including profiling, which produces legal effects concerning you or similarly significantly affects you (TabDivider currently does not engage in such automated decision-making that has legal or similarly significant effects)." }
              ].map(item => (
                <li key={item.title}>
                  <strong className="block text-gray-800 dark:text-gray-100">{item.title}</strong>
                  <p>{item.description}</p>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="5. How to Exercise Your Rights" id="exercising-rights">
            <p>
              To exercise any of your GDPR rights, or if you have any questions or concerns about our handling of your personal data, please contact us.
            </p>
            <p>
              You can manage some of your personal data directly through your TabDivider account settings. For example, if you registered an account (e.g., via Supabase), you may have options to update your profile information or delete your account. Account deletion will typically lead to the erasure of associated personal data, subject to our data retention policies and legal obligations.
            </p>
            <p>
              For requests that cannot be fulfilled through account settings, or for any other GDPR-related inquiries, please contact our Data Protection Officer (DPO) or privacy team at:
            </p>
            <p className="mt-2">
              <Mail className="inline-block h-5 w-5 mr-2 align-text-bottom" />
              Email: <a href={`mailto:${dpoEmail}`} className="text-blue-600 dark:text-blue-400 hover:underline font-medium">{dpoEmail}</a>
            </p>
            <p>
              We will respond to your request in accordance with GDPR requirements, typically within one month of receipt. We may need to verify your identity before processing your request.
            </p>
          </Section>

          <Section title="6. Data Transfers (If Applicable)" id="data-transfers">
            <p>
              [This section is crucial if you transfer data outside the EU/EEA. Example below assumes Supabase might store data in regions that require explanation. **VERIFY YOUR DATA STORAGE AND PROCESSING LOCATIONS.**]
            </p>
            <p>
              Your personal data may be stored and processed in any country where we have facilities or in which we engage service providers (like Supabase for database hosting). If we transfer your personal data outside the European Economic Area (EEA), we will take appropriate safeguards to ensure that your personal data remains protected in accordance with this GDPR information page and applicable data protection laws (e.g., by using Standard Contractual Clauses or relying on adequacy decisions).
            </p>
            <p>
              For more information on the safeguards we use for international data transfers, please contact us.
            </p>
          </Section>

          <Section title="7. Data Protection Officer (DPO)" id="dpo">
             <p>
              [If you are required to have a DPO or have appointed one voluntarily, include details here. Otherwise, state who handles data protection inquiries.]
            </p>
            <p>
              TabDivider has appointed a Data Protection Officer / privacy contact point who is responsible for overseeing questions in relation to this GDPR information and our <a href="/privacy-policy" className="text-blue-600 dark:text-blue-400 hover:underline font-medium">Privacy Policy</a>. If you have any questions or wish to exercise your legal rights, please contact them using the details provided above in the "How to Exercise Your Rights" section.
            </p>
          </Section>

          <Section title="8. Right to Lodge a Complaint" id="lodge-complaint">
            <p>
              If you have any concerns about our use of your personal information, you have the right to lodge a complaint with a supervisory authority, in particular in the EU Member State of your habitual residence, place of work, or place of the alleged infringement if you consider that the processing of personal data relating to you infringes the GDPR.
            </p>
            <p>
              However, we would appreciate the chance to deal with your concerns before you approach a supervisory authority, so please contact us in the first instance.
            </p>
          </Section>

          <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
            <FileText className="h-10 w-10 mx-auto mb-3 text-gray-500 dark:text-gray-400" />
            <p className="text-md text-gray-600 dark:text-gray-400">
              For more detailed information about our data practices, please review our complete <a href="/privacy-policy" className="text-blue-600 dark:text-blue-400 hover:underline font-medium">Privacy Policy</a>.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
              TabDivider is committed to upholding your data protection rights.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GDPRCompliance;